import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CUSTOMER_PORTAL_STUDENT_SEARCH_MIN_LENGTH,
  CUSTOMER_PORTAL_UPLOAD,
  CUSTOMER_REQUEST_CHANGE_KINDS,
  CUSTOMER_REQUEST_TYPES,
  getCustomerPortalAllowedExtensions,
} from "@routebot/constants";
import Button from "shared/components/FormElements/Button";
import useCustomerPortalApi from "./hooks/useCustomerPortalApi";
import {
  EXTRA_SERVICE_LIMITS,
  EXTRA_SERVICE_TYPE,
  extraServiceErrorKey,
  extraServiceFieldLabelKey,
  normalizeExtraService,
  requestTypeOptions,
} from "./extraService";
import "./CustomerPortal.css";

const EMPTY_TRIP = {
  date: "",
  origin: "",
  destination: "",
  departureTime: "",
  returnTime: "",
  headcount: "",
};

const CustomerRequestNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoading, error, createRequest, listSchools, searchStudents, listServiceNames } = useCustomerPortalApi();

  const [schools, setSchools] = useState([]);
  const [school, setSchool] = useState("");
  const [type, setType] = useState(EXTRA_SERVICE_TYPE);
  const [trip, setTrip] = useState(EMPTY_TRIP);
  const [changeKind, setChangeKind] = useState(CUSTOMER_REQUEST_CHANGE_KINDS.ADDRESS);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [student, setStudent] = useState(null);
  const [services, setServices] = useState([]);
  const [relatedRoute, setRelatedRoute] = useState("");
  const [files, setFiles] = useState([]);
  const [localError, setLocalError] = useState("");

  const isExtraService = type === EXTRA_SERVICE_TYPE;
  const needsStudent =
    !isExtraService &&
    (type === CUSTOMER_REQUEST_TYPES.STUDENT_REMOVE || type === CUSTOMER_REQUEST_TYPES.STUDENT_CHANGE);
  const typeOptions = useMemo(() => requestTypeOptions(), []);
  const accept = useMemo(
    () => getCustomerPortalAllowedExtensions().map((ext) => `.${ext}`).join(","),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    listSchools()
      .then((rows) => {
        if (cancelled) return;
        setSchools(rows);
        if (rows.length === 1) setSchool(rows[0].id);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [listSchools]);

  useEffect(() => {
    if (!school || isExtraService) {
      setServices([]);
      return;
    }
    let cancelled = false;
    listServiceNames(school)
      .then((rows) => {
        if (!cancelled) setServices(rows);
      })
      .catch(() => setServices([]));
    return () => {
      cancelled = true;
    };
  }, [school, isExtraService, listServiceNames]);

  useEffect(() => {
    setStudent(null);
    setQuery("");
    setMatches([]);
  }, [school, type]);

  useEffect(() => {
    if (!needsStudent || !school || query.trim().length < CUSTOMER_PORTAL_STUDENT_SEARCH_MIN_LENGTH) {
      setMatches((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const rows = await searchStudents(query.trim(), school);
        if (!student) setMatches(rows);
      } catch (err) {
        setMatches([]);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, school, needsStudent, searchStudents, student]);

  const onFiles = (event) => {
    const next = Array.from(event.target.files || []);
    const tooBig = next.find((file) => file.size > CUSTOMER_PORTAL_UPLOAD.MAX_FILE_BYTES);
    if (tooBig) {
      setLocalError(t("customerPortal.fileTooLarge"));
      return;
    }
    if (next.length > CUSTOMER_PORTAL_UPLOAD.MAX_FILES_PER_REQUEST) {
      setLocalError(t("customerPortal.tooManyFiles"));
      return;
    }
    setLocalError("");
    setFiles(next);
  };

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setLocalError("");
      if (!school) {
        setLocalError(t("customerPortal.schoolRequired"));
        return;
      }
      let payload;
      if (isExtraService) {
        const normalized = normalizeExtraService(trip);
        if (!normalized.ok) {
          const key = extraServiceErrorKey(normalized);
          setLocalError(
            normalized.reason === "too_long"
              ? t(key, { field: t(extraServiceFieldLabelKey(normalized.field)) })
              : t(key),
          );
          return;
        }
        const fields = normalized.value;
        payload = {
          type,
          school,
          extraServiceDate: fields.date,
          extraServiceOrigin: fields.origin,
          extraServiceDestination: fields.destination,
          extraServiceDepartureTime: fields.departureTime,
          extraServiceReturnTime: fields.returnTime,
          extraServiceHeadcount: fields.headcount,
          language: localStorage.getItem("i18nextLng") || "tr",
        };
      } else {
        if (!title.trim()) {
          setLocalError(t("customerPortal.titleRequired"));
          return;
        }
        if (needsStudent && !student) {
          setLocalError(t("customerPortal.studentRequired"));
          return;
        }
        const needsFiles =
          type === CUSTOMER_REQUEST_TYPES.STUDENT_ADD_BULK || type === CUSTOMER_REQUEST_TYPES.DOCUMENT;
        if (needsFiles && files.length === 0) {
          setLocalError(t("customerPortal.fileRequired"));
          return;
        }
        payload = {
          type,
          title: title.trim(),
          body: body.trim(),
          school,
          relatedStudent: student?.id || "",
          relatedRoute,
          changeKind: type === CUSTOMER_REQUEST_TYPES.STUDENT_CHANGE ? changeKind : "",
          language: localStorage.getItem("i18nextLng") || "tr",
        };
      }
      try {
        const created = await createRequest(
          payload,
          files,
        );
        const id = created?.id || created?._id;
        navigate(id ? `/portal/requests/${id}` : "/portal/requests");
      } catch (err) {
        setLocalError(err.message || t("customerPortal.saveFailed"));
      }
    },
    [title, school, needsStudent, student, type, isExtraService, trip, body, relatedRoute, changeKind, files, createRequest, navigate, t],
  );

  return (
    <div className="cp">
      <div className="cp-top">
        <div>
          <h1>{t("customerPortal.newRequest")}</h1>
          <p className="cp-muted">{t("customerPortal.newHint")}</p>
        </div>
        <Button to="/portal/requests" inverse containerStyle={{ marginTop: 0 }}>
          {t("customerPortal.backToList")}
        </Button>
      </div>

      {(localError || error) && <div className="cp-error">{localError || error}</div>}

      <form onSubmit={onSubmit} className="cp-card">
        <div className="cp-grid">
          <div>
            <label className="cp-label" htmlFor="cp-school">
              {t("customerPortal.school")}
            </label>
            <select id="cp-school" value={school} onChange={(event) => setSchool(event.target.value)} required>
              <option value="">{t("customerPortal.selectSchool")}</option>
              {schools.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="cp-label" htmlFor="cp-type">
              {t("customerPortal.type")}
            </label>
            <select
              id="cp-type"
              value={type}
              onChange={(event) => {
                const nextType = event.target.value;
                setType(nextType);
                setLocalError("");
                if (nextType === EXTRA_SERVICE_TYPE) setRelatedRoute("");
              }}
            >
              {typeOptions.map((value) => (
                <option key={value} value={value}>
                  {t(`customerPortal.types.${value}`, { defaultValue: value })}
                </option>
              ))}
            </select>
            {isExtraService && (
              <p className="cp-muted" style={{ marginTop: 6 }}>
                {t("customerPortal.extraServiceHint")}
              </p>
            )}
            {(type === CUSTOMER_REQUEST_TYPES.STUDENT_ADD_BULK || type === CUSTOMER_REQUEST_TYPES.DOCUMENT) && (
              <p className="cp-muted" style={{ marginTop: 6 }}>
                {type === CUSTOMER_REQUEST_TYPES.STUDENT_ADD_BULK
                  ? t("customerPortal.bulkHint")
                  : t("customerPortal.documentHint")}
              </p>
            )}
          </div>
        </div>

        {type === CUSTOMER_REQUEST_TYPES.STUDENT_CHANGE && (
          <div style={{ marginTop: 14 }}>
            <label className="cp-label" htmlFor="cp-change">
              {t("customerPortal.changeKind")}
            </label>
            <select id="cp-change" value={changeKind} onChange={(event) => setChangeKind(event.target.value)}>
              {Object.values(CUSTOMER_REQUEST_CHANGE_KINDS).map((value) => (
                <option key={value} value={value}>
                  {t(`customerPortal.changeKinds.${value}`)}
                </option>
              ))}
            </select>
          </div>
        )}

        {isExtraService && (
          <div className="cp-trip-fields">
            <div className="cp-trip-date">
              <label className="cp-label" htmlFor="cp-trip-date">
                {t("customerPortal.date")}
              </label>
              <input
                id="cp-trip-date"
                type="date"
                required
                min="2000-01-01"
                max="2100-12-31"
                value={trip.date}
                onChange={(event) => setTrip((prev) => ({ ...prev, date: event.target.value }))}
              />
            </div>
            <div>
              <label className="cp-label" htmlFor="cp-trip-origin">
                {t("customerPortal.tripOrigin")}
              </label>
              <input
                id="cp-trip-origin"
                type="text"
                required
                maxLength={EXTRA_SERVICE_LIMITS.place}
                autoComplete="off"
                placeholder={t("customerPortal.tripOriginPlaceholder")}
                value={trip.origin}
                onChange={(event) => setTrip((prev) => ({ ...prev, origin: event.target.value }))}
              />
            </div>
            <div>
              <label className="cp-label" htmlFor="cp-trip-destination">
                {t("customerPortal.tripDestination")}
              </label>
              <input
                id="cp-trip-destination"
                type="text"
                required
                maxLength={EXTRA_SERVICE_LIMITS.place}
                autoComplete="off"
                placeholder={t("customerPortal.tripDestinationPlaceholder")}
                value={trip.destination}
                onChange={(event) => setTrip((prev) => ({ ...prev, destination: event.target.value }))}
              />
            </div>
            <div className="cp-trip-times">
              <div>
                <label className="cp-label" htmlFor="cp-trip-departure">
                  {t("customerPortal.tripDeparture")}
                </label>
                <input
                  id="cp-trip-departure"
                  type="text"
                  required
                  maxLength={EXTRA_SERVICE_LIMITS.time}
                  autoComplete="off"
                  placeholder={t("customerPortal.tripDeparturePlaceholder")}
                  value={trip.departureTime}
                  onChange={(event) => setTrip((prev) => ({ ...prev, departureTime: event.target.value }))}
                />
              </div>
              <div>
                <label className="cp-label" htmlFor="cp-trip-return">
                  {t("customerPortal.tripReturn")}
                </label>
                <input
                  id="cp-trip-return"
                  type="text"
                  required
                  maxLength={EXTRA_SERVICE_LIMITS.time}
                  autoComplete="off"
                  placeholder={t("customerPortal.tripReturnPlaceholder")}
                  value={trip.returnTime}
                  onChange={(event) => setTrip((prev) => ({ ...prev, returnTime: event.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="cp-label" htmlFor="cp-trip-headcount">
                {t("customerPortal.tripHeadcount")}
              </label>
              <input
                id="cp-trip-headcount"
                type="text"
                required
                maxLength={EXTRA_SERVICE_LIMITS.headcount}
                autoComplete="off"
                placeholder={t("customerPortal.tripHeadcountPlaceholder")}
                value={trip.headcount}
                onChange={(event) => setTrip((prev) => ({ ...prev, headcount: event.target.value }))}
              />
            </div>
          </div>
        )}

        {!isExtraService && (
        <div style={{ marginTop: 14 }}>
          <label className="cp-label" htmlFor="cp-title">
            {t("customerPortal.title")}
          </label>
          <input id="cp-title" type="text" maxLength={200} value={title} onChange={(event) => setTitle(event.target.value)} />
        </div>
        )}

        {needsStudent && (
          <div className="cp-typeahead" style={{ marginTop: 14 }}>
            <label className="cp-label">{t("customerPortal.student")}</label>
            {student ? (
              <div>
                <strong>{student.name}</strong>
                <span className="cp-muted">
                  {" "}
                  {[student.identifier, student.level].filter(Boolean).join(" · ")}
                </span>{" "}
                <Button
                  type="button"
                  inverse
                  size="small"
                  onClick={() => {
                    setStudent(null);
                    setQuery("");
                  }}
                >
                  {t("customerPortal.clearStudent")}
                </Button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t("customerPortal.studentSearchPlaceholder")}
                  autoComplete="off"
                />
                {matches.length > 0 && (
                  <ul className="cp-typeahead-list">
                    {matches.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setStudent(item);
                            setMatches([]);
                            setQuery(item.name);
                          }}
                        >
                          {item.name}
                          {item.identifier ? ` · ${item.identifier}` : ""}
                          {item.level ? ` · ${item.level}` : ""}
                          {item.isActive === false ? ` (${t("customerPortal.inactive")})` : ""}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        {!isExtraService && (
          <div style={{ marginTop: 14 }}>
            <label className="cp-label" htmlFor="cp-route">
              {t("customerPortal.serviceOptional")}
            </label>
            <select id="cp-route" value={relatedRoute} onChange={(event) => setRelatedRoute(event.target.value)}>
              <option value="">{t("customerPortal.noService")}</option>
              {services.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {!isExtraService && (
          <div style={{ marginTop: 14 }}>
            <label className="cp-label" htmlFor="cp-body">
              {t("customerPortal.details")}
            </label>
            <textarea id="cp-body" maxLength={5000} value={body} onChange={(event) => setBody(event.target.value)} />
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <label className="cp-label" htmlFor="cp-files">
            {t("customerPortal.attachments")}
          </label>
          <input id="cp-files" type="file" multiple accept={accept} onChange={onFiles} />
          {files.length > 0 && (
            <ul className="cp-file-picked">
              {files.map((file) => (
                <li key={`${file.name}-${file.size}`}>{file.name}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="cp-actions">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? t("customerPortal.sending") : t("customerPortal.submit")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CustomerRequestNew;
